import { Extension, InputRule } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { parse } from "../lib/fill_blank_shortcut_parser"

const SHORTCUT_REGEX = /\[([^\[\]]+)\]$/
const GLOBAL_SHORTCUT_REGEX = /\[([^\[\]]+)\]/g

export function createFillBlankShortcutExtension({ getMode, getBlanks, generateBlankId, syncBlanks }) {
  return Extension.create({
    name: "fillBlankShortcut",

    addInputRules() {
      return [
        new InputRule({
          find: SHORTCUT_REGEX,
          handler: ({ state, range, match, chain }) => {
            const inner = match[1]
            const mode = getMode()
            const entry = parse(inner, mode)
            const blankId = ensureUniqueId(getBlanks(), generateBlankId)

            const blanks = getBlanks()
            blanks[blankId] = mode === "multiple_choice"
              ? entry
              : { answer: entry.answer, hint: entry.hint, alternatives: entry.alternatives }
            syncBlanks()

            const fillBlankType = state.schema.nodes.fillBlank
            if (!fillBlankType) return null

            chain()
              .deleteRange({ from: range.from, to: range.to })
              .insertContentAt(range.from, {
                type: "fillBlank",
                attrs: { "data-blank-id": blankId },
              })
              .run()
          },
        }),
      ]
    },

    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey("fillBlankShortcutPaste"),
          props: {
            handlePaste: (view, _event, slice) => {
              const fillBlankType = view.state.schema.nodes.fillBlank
              if (!fillBlankType) return false

              const mode = getMode()
              const blanks = getBlanks()
              const reservedIds = new Set(Object.keys(blanks))
              let mutated = false

              const transformFragment = (fragment) => {
                const out = []
                fragment.forEach((child) => {
                  if (child.isText && child.text) {
                    const text = child.text
                    const matches = [...text.matchAll(GLOBAL_SHORTCUT_REGEX)]
                    if (matches.length === 0) {
                      out.push(child)
                      return
                    }
                    mutated = true
                    let lastIndex = 0
                    for (const m of matches) {
                      if (m.index > lastIndex) {
                        out.push(child.cut(lastIndex, m.index))
                      }
                      const inner = m[1]
                      const entry = parse(inner, mode)
                      const blankId = ensureUniqueId(
                        Object.fromEntries([...reservedIds].map((id) => [id, true])),
                        generateBlankId
                      )
                      reservedIds.add(blankId)
                      blanks[blankId] = mode === "multiple_choice"
                        ? entry
                        : { answer: entry.answer, hint: entry.hint, alternatives: entry.alternatives }
                      out.push(fillBlankType.create({ "data-blank-id": blankId }))
                      lastIndex = m.index + m[0].length
                    }
                    if (lastIndex < text.length) {
                      out.push(child.cut(lastIndex))
                    }
                  } else if (child.content && child.content.size > 0) {
                    out.push(child.copy(transformFragment(child.content)))
                  } else {
                    out.push(child)
                  }
                })
                return fragment.constructor.fromArray(out)
              }

              const newContent = transformFragment(slice.content)
              if (!mutated) return false
              syncBlanks()
              const newSlice = new slice.constructor(newContent, slice.openStart, slice.openEnd)
              view.dispatch(
                view.state.tr
                  .replaceSelection(newSlice)
                  .scrollIntoView()
                  .setMeta("paste", true)
                  .setMeta("uiEvent", "paste")
              )
              return true
            },
          },
        }),
      ]
    },
  })
}

function ensureUniqueId(blanks, generate) {
  let id = generate()
  let attempts = 0
  while (blanks[id] && attempts < 50) {
    id = generate()
    attempts += 1
  }
  return id
}
